import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

/**
 * Dialog that lets a guest INCREASE person counts on their PENDING booking.
 * Calls PATCH /api/bookings/{id}/persons. Decreases are rejected by the API.
 */
export default function EditPersonsDialog({ open, onOpenChange, booking, onUpdated }) {
  const isCabinBooking = (booking?.cabin_persons || 0) > 0;
  const [adults, setAdults] = useState(booking?.adults ?? 0);
  const [children, setChildren] = useState(booking?.children ?? 0);
  const [cars, setCars] = useState(booking?.cars ?? 0);
  const [tents, setTents] = useState(booking?.tents ?? 0);
  const [cabinPersons, setCabinPersons] = useState(booking?.cabin_persons ?? 0);
  const [saving, setSaving] = useState(false);

  // Re-sync when the dialog opens with a fresh booking
  React.useEffect(() => {
    if (open) {
      setAdults(booking?.adults ?? 0);
      setChildren(booking?.children ?? 0);
      setCars(booking?.cars ?? 0);
      setTents(booking?.tents ?? 0);
      setCabinPersons(booking?.cabin_persons ?? 0);
    }
  }, [open, booking]);

  const submit = async () => {
    setSaving(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/bookings/${booking.id}/persons`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          adults: isCabinBooking ? undefined : adults,
          children: isCabinBooking ? undefined : children,
          cars,
          tents,
          cabin_persons: isCabinBooking ? cabinPersons : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Chyba ${res.status}`);
      toast.success(`Rezervace upravena. Nová cena: ${data.total_price} Kč`);
      onOpenChange(false);
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e.message || 'Chyba při úpravě rezervace');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="edit-persons-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Upravit počet osob</AlertDialogTitle>
          <AlertDialogDescription>
            Před schválením hostitelem můžete <strong>navýšit</strong> počet osob. Snížit ani změnit datum nelze – pro zrušení rezervaci zrušte a vytvořte novou. Cena se automaticky přepočítá.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          {isCabinBooking ? (
            <div className="col-span-2">
              <Label htmlFor="ep-cabin">🏠 Osob v chatě / maringotce / teepee</Label>
              <Input
                id="ep-cabin"
                type="number"
                min={booking.cabin_persons || 1}
                value={cabinPersons}
                onChange={(e) => setCabinPersons(Math.max(0, parseInt(e.target.value) || 0))}
                data-testid="edit-cabin-persons"
              />
              <p className="text-xs text-gray-500 mt-1">Původně: {booking.cabin_persons}</p>
            </div>
          ) : (
            <>
              <div>
                <Label htmlFor="ep-adults">Dospělí</Label>
                <Input
                  id="ep-adults"
                  type="number"
                  min={booking.adults || 1}
                  value={adults}
                  onChange={(e) => setAdults(Math.max(0, parseInt(e.target.value) || 0))}
                  data-testid="edit-adults"
                />
                <p className="text-xs text-gray-500 mt-1">Původně: {booking.adults || 0}</p>
              </div>
              <div>
                <Label htmlFor="ep-children">Děti</Label>
                <Input
                  id="ep-children"
                  type="number"
                  min={booking.children || 0}
                  value={children}
                  onChange={(e) => setChildren(Math.max(0, parseInt(e.target.value) || 0))}
                  data-testid="edit-children"
                />
                <p className="text-xs text-gray-500 mt-1">Původně: {booking.children || 0}</p>
              </div>
            </>
          )}
          <div>
            <Label htmlFor="ep-cars">Auta</Label>
            <Input
              id="ep-cars"
              type="number"
              min={booking.cars || 0}
              value={cars}
              onChange={(e) => setCars(Math.max(0, parseInt(e.target.value) || 0))}
              data-testid="edit-cars"
            />
            <p className="text-xs text-gray-500 mt-1">Původně: {booking.cars || 0}</p>
          </div>
          <div>
            <Label htmlFor="ep-tents">Stany</Label>
            <Input
              id="ep-tents"
              type="number"
              min={booking.tents || 0}
              value={tents}
              onChange={(e) => setTents(Math.max(0, parseInt(e.target.value) || 0))}
              data-testid="edit-tents"
            />
            <p className="text-xs text-gray-500 mt-1">Původně: {booking.tents || 0}</p>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving} data-testid="edit-persons-cancel">Zrušit</AlertDialogCancel>
          <AlertDialogAction onClick={submit} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="edit-persons-save">
            {saving ? 'Ukládání…' : 'Uložit změny'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
